import { useState, useMemo } from 'react'
import { Eye } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useUserCheck } from '../../hooks/api/useUserCheck'
import { useAllSubscriptions } from '../../hooks/api/useBilling'
import { DashboardLayout } from '../../components/layout/DashboardLayout'
import { FilterDropdown, FilterState } from '../../components/ui/FilterDropdown'

const subscriptionStatuses = ['All', 'Active', 'Canceled'] as const
type SubscriptionStatus = typeof subscriptionStatuses[number]

// Subscription status options for billing
const SUBSCRIPTION_STATUS_OPTIONS = [
  { value: null, label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'canceled', label: 'Canceled' }
]

// Helper to format date
const formatDate = (dateString: string | undefined) => {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const BillingSubscriptionsPage = () => {
  const { user } = useAuth()
  const { data: userRecord, isLoading, error } = useUserCheck(user?.id || '')
  const { data: subscriptions, isLoading: subsLoading } = useAllSubscriptions()
  const [selectedStatus, setSelectedStatus] = useState<SubscriptionStatus>('All')
  const [currentPage, setCurrentPage] = useState(1)
  const [filters, setFilters] = useState<FilterState>({
    organization: null,
    status: null,
    priority: null
  })
  const itemsPerPage = 20

  // Get unique organizations from subscriptions
  const organizations = useMemo(() => {
    if (!subscriptions) return []
    const unique = new Set(subscriptions.map((s: any) => s.companies?.name).filter(Boolean))
    return Array.from(unique) as string[]
  }, [subscriptions])

  // Filter subscriptions based on selected status and filters
  const filteredSubscriptions = useMemo(() => {
    let result = subscriptions || []

    // Tab filter
    if (selectedStatus !== 'All') {
      result = result.filter((sub: any) =>
        sub.status.toLowerCase() === selectedStatus.toLowerCase()
      )
    }

    // Organization filter
    if (filters.organization) {
      result = result.filter((sub: any) =>
        sub.companies?.name === filters.organization
      )
    }

    // Status filter (same as tab filter, but from dropdown)
    if (filters.status) {
      result = result.filter((sub: any) =>
        sub.status === filters.status
      )
    }

    return result
  }, [subscriptions, selectedStatus, filters])

  // Calculate stats from real data
  const activeCount = subscriptions?.filter((s: any) => s.status === 'active').length || 0
  const canceledCount = subscriptions?.filter((s: any) => s.status === 'canceled').length || 0

  if (isLoading || subsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-3 border-gray-400 border-t-transparent border-r-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading subscriptions...</p>
        </div>
      </div>
    )
  }

  if (error || !userRecord) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-600 text-sm font-medium mb-2">User not found in database!</p>
            <p className="text-red-500 text-xs">Please contact support.</p>
          </div>
        </div>
      </div>
    )
  }

  // Pagination
  const totalPages = Math.ceil(filteredSubscriptions.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedSubscriptions = filteredSubscriptions.slice(startIndex, endIndex)

  return (
    <DashboardLayout
      userRole={userRecord.role}
      adminLevel={userRecord.admin_level}
      userName={userRecord.full_name || user?.email}
      userEmail={user?.email}
    >
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscriptions</h1>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Active Subscriptions */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="text-sm text-gray-600 mb-2">Active Subscriptions</div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{activeCount}</div>
            <div className="text-sm text-gray-600">Currently active</div>
          </div>

          {/* Cancelled Subscriptions */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="text-sm text-gray-600 mb-2">Cancelled Subscriptions</div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{canceledCount}</div>
            <div className="text-sm text-gray-600">Total canceled</div>
          </div>

          {/* Total Subscriptions */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="text-sm text-gray-600 mb-2">Total Subscriptions</div>
            <div className="text-3xl font-bold text-gray-900">{subscriptions?.length || 0}</div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          {/* Filter Button and Status Tabs */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex space-x-2 border-b border-gray-200">
              {subscriptionStatuses.map((status) => (
                <button
                  key={status}
                  onClick={() => {
                    setSelectedStatus(status)
                    setCurrentPage(1)
                  }}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    selectedStatus === status
                      ? 'border-gray-900 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
            <FilterDropdown
              filters={filters}
              onFilterChange={setFilters}
              organizations={organizations}
              statusOptions={SUBSCRIPTION_STATUS_OPTIONS}
              showPriority={false}
              storageKey="billing-subscriptions-filters"
            />
          </div>

          {/* Subscriptions Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Next Payment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedSubscriptions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      No subscriptions found
                    </td>
                  </tr>
                ) : (
                  paginatedSubscriptions.map((subscription: any) => (
                    <tr key={subscription.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {subscription.companies?.name || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {subscription.tier.charAt(0).toUpperCase() + subscription.tier.slice(1)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          subscription.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : subscription.status === 'past_due'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        N/A
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(subscription.current_period_end)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button className="text-gray-400 hover:text-gray-600 transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-6 flex items-center justify-between text-sm text-gray-600">
            <div>Showing {startIndex + 1} to {Math.min(endIndex, filteredSubscriptions.length)} of {filteredSubscriptions.length} alerts</div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1 border border-gray-300 rounded ${
                  currentPage === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'hover:bg-gray-50'
                }`}
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 border border-gray-300 rounded ${
                  currentPage === totalPages
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'hover:bg-gray-50'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
